/* eslint-disable no-underscore-dangle */
import React, {
  memo, useCallback, useMemo, useState,
} from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer, TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faCalendarPlus } from '@fortawesome/free-solid-svg-icons';
import { useTablePart } from '../../../../BaseEditor/tablePart/hook';
import TPCommandPanel from '../../../../BaseEditor/tablePart/commandPanels/materialCommandPanel';
import Row from './row';
import { EditorControls } from '../../../../BaseEditor';

function pagesSelector(rows) {
  const cRows = rows.map((row) => ({ ...row, _date: new Date(row.date) }));
  const years = new Set(cRows.map((row) => row._date.getFullYear()));
  return [...years].sort()
    .map((y) => ({
      label: `${y} р.`,
      id: y,
      rows: cRows.filter((row) => row._date.getFullYear() === y)
        .map(({ _date, ...row }) => row),
    }));
}

const defaultRecord = () => ({
  date: new Date().toJSON().slice(0, 10),
  order_amount: 0,
});

function PaymentTablePart({
  data, options, onChange, readOnly,
}) {
  const {
    displayedItems, fields, actions, permissions, activeRow,
    pages, currentPage,
  } = useTablePart({
    data, options, onChange, readOnly, pagesSelector, defaultRecord,
  });
  const total = useMemo(
    () => displayedItems
      .reduce((R, row) => R + parseFloat(row.order_amount), 0)
      .toLocaleString('uk', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [displayedItems],
  );

  const [modal, setModal] = useState({ opened: false, mode: 1 });
  const [mdata, setMData] = useState({ sum: 0, months: 12, start: new Date().toJSON().slice(0, 10) });

  const fillSum = useCallback(
    (e) => {
      const newData = [...Array(mdata.months).keys()].reduce((R, _, i) => {
        const cSum = modal.mode === 1 ? Math.round((R.rSum / R.rq) * 100) / 100 : mdata.sum;
        const nextDate = new Date(mdata.start);
        nextDate.setMonth(new Date(mdata.start).getMonth() + i);
        nextDate.setMinutes(-nextDate.getTimezoneOffset());
        return {
          data: [
            ...R.data,
            {
              date: nextDate.toISOString().slice(0, 10),
              order_amount: cSum.toFixed(2),
              payment_notes: '',
            },
          ],
          rSum: R.rSum - cSum,
          rq: R.rq - 1,
          cDate: nextDate,
        };
      }, {
        data: [],
        rSum: mdata.sum,
        rq: mdata.months,
      });
      const dates = new Set(newData.data.map((r) => r.date));
      onChange(e, [
        ...(data || []).filter((r) => !dates.has(r.date)),
        ...newData.data,
      ]);
      setModal((d) => ({ ...d, opened: false }));
    },
    [data, mdata.months, mdata.start, mdata.sum, modal.mode, onChange],
  );

  return (
    <>
      <TPCommandPanel
        actions={actions}
        permissions={permissions}
        pages={pages}
        currentPage={currentPage}
        activeRow={activeRow}
      >
        <Button
          onClick={() => setModal({ opened: true, mode: 1 })}
          startIcon={(<FontAwesomeIcon icon={faFileInvoiceDollar} />)}
          variant="outlined"
          size="small"
        >
          Додати рівними долями
        </Button>
        <Button
          onClick={() => setModal({ opened: true, mode: 2 })}
          startIcon={<FontAwesomeIcon icon={faCalendarPlus} />}
          variant="outlined"
          size="small"
        >
          Заповнити суму за періодом
        </Button>
      </TPCommandPanel>
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                {fields.date.label}
              </TableCell>
              <TableCell>
                {fields.order_amount.label}
              </TableCell>
              <TableCell>
                {fields.payment_notes.label}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedItems.map((row) => (
              <Row
                actions={actions}
                row={row}
                readOnly={readOnly}
                fields={fields}
                key={row.id}
                active={activeRow === row.id}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell align="right">
                Разом:
              </TableCell>
              <TableCell align="right">
                {total}
              </TableCell>
              <TableCell>
                &nbsp;
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <Dialog open={modal.opened} onClose={() => setModal((d) => ({ ...d, opened: false }))}>
        <DialogTitle>Заповнення</DialogTitle>
        <DialogContent>
          <EditorControls.NumerInput
            label={modal.mode === 1 ? 'Розподілити рівномірно суму (грн)' : 'Заповнити сумою  (грн)'}
            value={mdata.sum}
            precision={2}
            onChange={(e, v) => setMData((d) => ({ ...d, sum: v }))}
          />
          <EditorControls.NumerInput
            label="на (місяців)"
            value={mdata.months}
            precision={0}
            minValue={1}
            maxValue={99}
            onChange={(e, v) => setMData((d) => ({ ...d, months: v }))}
          />
          <EditorControls.DateInput
            label="Починаючи з"
            value={mdata.start}
            onChange={(e, v) => setMData((d) => ({ ...d, start: v }))}
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={fillSum}>
            OK
          </Button>
          <Button color="secondary" onClick={() => setModal((d) => ({ ...d, opened: false }))}>
            Відміна
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

PaymentTablePart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({

  })).isRequired,
  options: PropTypes.shape({
    child: PropTypes.shape({
      children: PropTypes.shape(),
    }),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

PaymentTablePart.defaultProps = {
  readOnly: false,
};

export default memo(PaymentTablePart);
